import { minDistToPolygon, pointInPolygon } from "@nova/calculate-geospatial"
import { HotspotStatus } from "@nova/constants"
import { Hotspot, Polygon } from "@nova/protobuf"
import { createPlacemark, hotspotsToKML, PlacemarkBuilder } from "./hotspotsToKML"
import { IntterraPlacemarkBuilder } from "./IntterraPlacemarkBuilder"
import { TAKHotspotOptions, TAKPlacemarkBuilder } from "./TAKPlacemarkBuilder"

export interface KMLOptions {
    relativeSizeEnabled?: boolean
    disableLabels?: boolean
}

export type { TAKHotspotOptions }

export const hotspotsToIntterraKML = (hotspots: Point[], options: KMLOptions) =>
    hotspotsToKML(hotspots, IntterraPlacemarkBuilder(options))
export const hotspotsToTAKKML = (hotspots: Point[], options: TAKHotspotOptions) =>
    hotspotsToKML(hotspots, TAKPlacemarkBuilder(options))

// Default use case
export const createHotspotsKML = (hotspots: Point[], options: KMLOptions) =>
    hotspotsToKML(hotspots, DefaultPlacemarkBuilder(options))

const DefaultPlacemarkBuilder = (options: KMLOptions): PlacemarkBuilder => ({
    build: (id, point) => createPlacemark(id, point, options),
    buildStyle: () => `
    <Style id="redCircle">
        <IconStyle>
            <colorMode>normal</colorMode>
            <Icon>
                <href>https://cdn.mapnova.com/icons/isolated-heat.png</href>
            </Icon>
        </IconStyle>
        ${
            options.disableLabels
                ? `
        <LabelStyle>
            <scale>0</scale>
        </LabelStyle>`
                : ""
        }
    </Style>`,
})
export interface Point {
    label: string
    latitude: number
    longitude: number
    status: HotspotStatus
    color?: number[]
    deletedAt?: string
    sizeIndex?: number
}

export function getHotspotPerimeterDistances(
    hotspots: Hotspot[],
    perimeter?: Polygon[]
): (number | undefined)[] {
    if (!perimeter || perimeter.length === 0 || !hotspots || hotspots.length === 0) return []
    perimeter = perimeter.map((e) => ({ coordinates: e.coordinates.slice(0, -1) }))
    const map = hotspots.reduce(
        (acc, cur, idx) => {
            acc[cur.id] = idx
            return acc
        },
        {} as Record<string, number>
    )
    const points = hotspots.filter((point) =>
        perimeter.some((polygon) => pointInPolygon(point, polygon.coordinates))
    )
    const distances = perimeter.map((poly) =>
        points.map((point) => minDistToPolygon(point, poly.coordinates))
    )

    const minDistances: (number | undefined)[] = Array.from({ length: hotspots.length }).map(
        () => undefined
    )
    for (let i = 0; i < distances.length; i++) {
        for (let j = 0; j < points.length; j++) {
            const originalIdx = map[points[j].id]
            const current = minDistances[originalIdx]
            if (current === undefined) minDistances[originalIdx] = distances[i][j]
            else if (distances[i][j]) minDistances[originalIdx] = Math.min(current, distances[i][j])
        }
    }
    return minDistances
}
